'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AppBar, Box, Toolbar } from '@mui/material';

import {
  AppHeader,
  BottomNavigation,
  CategoryNavLinks,
  Footer,
  GeoBlockBanner,
  MobileMenuDrawer,
  ModalsOrchestrator,
  ProxyWalletInitializer,
  ReferralRibbon,
} from '@/components';
import GuestClickGuard from '@/components/specific/auth/GuestClickGuard';
import { useTheme as useAppTheme } from '@/contexts/ThemeContext';
import { useAuth, useSyncBookmarks } from '@/hooks';
import { useLayoutMobileState, useLayoutModals, useLayoutSearch } from '@/hooks/layout';
import { useGuestGate } from '@/hooks/useGuestGate';
import { usePortfolioSummary } from '@/services/query';
import { useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';

import { LAYOUT_MAX_WIDTH } from '@/constants';

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * MainLayout - Primary application layout wrapper.
 */
export const MainLayout = ({ children }: MainLayoutProps) => {
  const { mode, toggleTheme } = useAppTheme();

  const { user, isAuthenticated, isLoading } = useAuth();
  useSyncBookmarks(isAuthenticated);
  const {
    data: portfolioSummary,
    isLoading: isQueryLoading,
    effectiveProxy,
  } = usePortfolioSummary();

  const modals = useLayoutModals();

  const search = useLayoutSearch();
  const { mobileMenuDrawer } = useLayoutMobileState();

  const searchInputRef = useRef<HTMLDivElement>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const isLoadingPortfolio =
    isInitialDelay ||
    isQueryLoading ||
    portfolioSummary === undefined ||
    !effectiveProxy ||
    (portfolioSummary as any).isPlaceholder;

  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();

  // Signed-out users may only view the landing page. `isBlockedRoute` is true when
  // a guest is on any other route; the effect below sends them home and opens login.
  const { isBlockedRoute } = useGuestGate();

  useEffect(() => {
    if (!isBlockedRoute) return;
    modals.auth.open();
    router.replace('/');
    // Only the trigger matters here; re-running on modals/router identity changes
    // would just re-fire an idempotent redirect.
  }, [isBlockedRoute]);

  const handleLoginClick = () => {
    modals.auth.open();
  };

  const handleDepositClick = () => {
    if (!isAuthenticated) {
      modals.auth.open();
      return;
    }
    if (!user?.isReferredUser) {
      modals.referral.open();
      return;
    }
    dispatch(setSetupRequested(true));
    modals.deposit.open();
  };

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMoreOpen((prev) => !prev);
  };

  const handleMoreClose = () => {
    setIsMoreOpen(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialDelay(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ProxyWalletInitializer />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            width: '100%',
            bgcolor: 'background.default',
          }}
        >
          <AppBar
            position="static"
            elevation={0}
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              backdropFilter: 'blur(8px)',
              width: '100%',
            }}
          >
            <ReferralRibbon />
            <GeoBlockBanner />
            <Toolbar sx={{ p: 0, width: '100%' }}>
              <AppHeader
                searchInputRef={searchInputRef}
                isAuthenticated={isAuthenticated}
                isLoading={isLoading}
                portfolioValue={portfolioSummary?.totalValue || 0}
                cashBalance={portfolioSummary?.cashBalance || 0}
                isLoadingPortfolio={isLoadingPortfolio}
                onSearchClick={search.onSearchClick}
                searchQuery={search.searchQuery}
                onSearchChange={search.onSearchChange}
                onSearchSubmit={search.onSearchSubmit}
                onHowItWorksOpen={modals.howItWorks.open}
                onLoginClick={handleLoginClick}
                onDepositModalOpen={handleDepositClick}
                onReferralModalOpen={modals.referral.open}
                isReferredUser={user?.isReferredUser}
                isAuthSyncing={false}
              />
            </Toolbar>
          </AppBar>
          <GuestClickGuard>
            <CategoryNavLinks
              isMoreOpen={isMoreOpen}
              moreButtonRef={moreButtonRef}
              onMoreClick={handleMoreClick}
              onMoreClose={handleMoreClose}
            />
          </GuestClickGuard>
        </Box>
        <Box
          component="main"
          sx={{
            width: '100%',
            maxWidth: LAYOUT_MAX_WIDTH,
            mx: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
          }}
        >
          {/* Guests only ever see the landing page. On a blocked route the effect
              above is redirecting home, so render nothing to avoid flashing gated
              content. On allowed pages, the guard turns interactive clicks into a
              login prompt. */}
          {isBlockedRoute ? null : <GuestClickGuard>{children}</GuestClickGuard>}
        </Box>
        <Footer onHowItWorksOpen={modals.howItWorks.open} isLoading={isLoading} />
        <MobileMenuDrawer
          isOpen={mobileMenuDrawer.isOpen}
          onClose={mobileMenuDrawer.close}
          isDarkMode={mode === 'dark'}
          onThemeToggle={toggleTheme}
        />
        <ModalsOrchestrator
          auth={{ isOpen: modals.auth.isOpen, onClose: modals.auth.onClose }}
          deposit={{ isOpen: modals.deposit.isOpen, onClose: modals.deposit.onClose }}
          howItWorks={{ isOpen: modals.howItWorks.isOpen, onClose: modals.howItWorks.onClose }}
          search={{
            isOpen: search.isDropdownOpen,
            onClose: search.onDropdownClose,
            anchorRef: searchInputRef,
            searchQuery: search.searchQuery,
          }}
          referral={{ isOpen: modals.referral.isOpen, onClose: modals.referral.onClose }}
        />
        <BottomNavigation
          onMenuClick={mobileMenuDrawer.toggle}
          onDepositClick={handleDepositClick}
          isLoading={isLoading}
        />
      </Box>
    </>
  );
};

export default MainLayout;
